import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import toast from "react-hot-toast";
import { Input, Select, Switch, Button } from '../ui';
import { FormField } from '../ui/Form';
import SectionTitle from '../ui/SectionTitle';
import { 
  CubeIcon, 
  CurrencyDollarIcon, 
  ScaleIcon, 
  InformationCircleIcon,
  TagIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusSmallIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import type { OfimaPrecioItem } from '../../types/product'
import { ofimaCodigosPrecio } from '../../assets/data/arrays'
import {
  normalizeTaxRatePercent,
  getTaxRateSelectValue,
  isValidProductTaxRate
} from '../../utils/productTaxRateUtils'

/** Lista Ofima `ofimaCodigosPrecio`: Venta General → mismo precio base que el producto (sin IVA). */
const OFIMA_COD_VENTA_GENERAL = '001'

/** Opciones de código por fila: no se repite un código ya elegido en otra fila. */
function getOfimaPrecioCodigoOptionsForRow(rows: OfimaPrecioItem[], rowIndex: number) {
  const current = String(rows[rowIndex]?.codigoPrecio ?? '').trim()
  const usedElsewhere = new Set<string>()
  rows.forEach((r, i) => {
    if (i === rowIndex) return
    const c = String(r.codigoPrecio).trim()
    if (c) usedElsewhere.add(c)
  })
  return [
    { value: '', label: 'Seleccionar código' },
    ...ofimaCodigosPrecio.filter(
      (o) => !usedElsewhere.has(o.value) || o.value === current
    )
  ]
}

/** Acorta mensajes de error de integración (JSON embebido en `detalles:`). */
function simplifyProductApiErrorDetail(raw: string): string {
  const t = String(raw).replace(/\s*\|\s*detalles:[\s\S]*$/i, '').trim()
  if (t.length > 220) return `${t.slice(0, 217)}…`
  return t || 'Error sin detalle'
}

/** Mensaje legible cuando el API devuelve `message` + `errors[]` (creación masiva / Ofima). */
function getApiProductErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>
  const headline = typeof d.message === 'string' ? d.message.trim() : ''
  const errors = d.errors
  if (!Array.isArray(errors) || errors.length === 0) {
    return headline
  }
  const lines = errors.map((item) => {
    if (!item || typeof item !== 'object') return ''
    const row = item as Record<string, unknown>
    const sku = row.sku != null ? String(row.sku).trim() : ''
    const errRaw = row.error != null ? String(row.error) : ''
    const err = simplifyProductApiErrorDetail(errRaw)
    if (sku && err) return `${sku}: ${err}`
    if (err) return err
    return sku || ''
  }).filter(Boolean)
  if (lines.length === 0) return headline
  const body = lines.join(' · ')
  if (headline && body) return `${headline} — ${body}`
  return body || headline
}

interface Component {
  sku: string
  quantity: number
  name?: string
  /** API puede enviar número o string */
  price?: number | string
}
interface Product {
  sku: string;
  name: string;
  description: string;
  category_id: number;
  price: number;
  is_combo: boolean;
  tax_rate: number;
  ean12: string;
  peso: string; // Cambiado a string
  longitud: string; // Cambiado a string
  ancho: string; // Cambiado a string
  alto: string; // Cambiado a string
  instrucciones_empaque: string;
  imagen: string;
  components: Component[];
  estado: number
  crear_en_ofima: number
  idUnidad: string
  precios: OfimaPrecioItem[]
  product_id?: number
}

interface ProductFormProps {}

/** Normaliza flags 0/1 desde API (número, string o booleano). */
const toBit = (value: unknown, whenMissing: number): number => {
  if (value === true) return 1
  if (value === false) return 0
  if (value === undefined || value === null) return whenMissing
  const n = Number(value)
  return n === 1 ? 1 : 0
}

const ProductForm: React.FC<ProductFormProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product>({
    sku: '',
    name: '',
    description: '',
    category_id: 1,
    price: 0,
    is_combo: false,
    tax_rate: 0,
    ean12: '',
    peso: '', // Cambiado a string
    longitud: '', // Cambiado a string
    ancho: '', // Cambiado a string
    alto: '', // Cambiado a string
    instrucciones_empaque: '',
    imagen: '',
    components: [],
    estado: 1,
    crear_en_ofima: 1,
    idUnidad: 'UND',
    precios: []
  });
  //const [newComponent, setNewComponent] = useState<Component>({ sku: '', quantity: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isHydrating, setIsHydrating] = useState(() => Boolean(id));
  const isEditMode = Boolean(id)

  const ofimaTodosLosCodigosAsignados = useMemo(() => {
    const used = new Set(
      product.precios.map((p) => String(p.codigoPrecio).trim()).filter(Boolean)
    )
    return ofimaCodigosPrecio.every((o) => used.has(o.value))
  }, [product.precios])

  /** Venta General (001): precio de lista = precio del producto (sin IVA), siempre alineado. */
  useEffect(() => {
    const base = Number(product.price) || 0
    setProduct((prev) => {
      let changed = false
      const next = prev.precios.map((row) => {
        if (String(row.codigoPrecio).trim() !== OFIMA_COD_VENTA_GENERAL) return row
        if (row.precio !== base) changed = true
        return { ...row, precio: base }
      })
      if (!changed) return prev
      return { ...prev, precios: next }
    })
  }, [product.price])

  useEffect(() => {
    if (isEditMode) {
      setIsHydrating(true);
      const fetchProduct = async () => {
        try {
          const response = await axiosInstance.get(`/products/${id}?SinSaldo=true`);
          const d = response.data
          const productData: Product = {
            ...d,
            product_id: Number(d.product_id),
            components: Array.isArray(d.components) ? d.components : [],
            estado: toBit(d.estado, 1),
            crear_en_ofima: toBit(d.crear_en_ofima, 1),
            tax_rate: normalizeTaxRatePercent(d.tax_rate, 5),
            idUnidad:
              d.idUnidad !== undefined && d.idUnidad !== null && String(d.idUnidad).trim() !== ''
                ? String(d.idUnidad)
                : 'UND',
            precios: Array.isArray(d.precios) ? d.precios : []
          }
          setProduct(productData);
        } catch (err) {
          console.error('Error obteniendo producto:', err);
          toast.error('No se pudo cargar el producto')
          navigate('/products')
        } finally {
          setIsHydrating(false);
        }
      };
      fetchProduct();
    } else {
      setIsHydrating(false);
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setProduct({ ...product, [name]: checked });
    } else if (["peso", "longitud", "ancho", "alto"].includes(name)) {
      setProduct({ ...product, [name]: value.replace(/,/g, '.') });
    } else if (type === 'number') {
      const numericValue = parseFloat(value);
      setProduct({ ...product, [name]: isNaN(numericValue) ? 0 : numericValue });
    } else {setProduct({ ...product, [name]: value });}
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProduct({ ...product, [name]: parseInt(value) || 0 });
  };

/*   const handleComponentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      const numericValue = parseInt(value);
      setNewComponent({ ...newComponent, [name]: isNaN(numericValue) ? 1 : numericValue });
    } else {
      setNewComponent({ ...newComponent, [name]: value });
    }
  }; */

  const handleSearch = async (term: string) => {
    if (term.length >= 4) {
      setIsSearching(true);
      try {
        const response = await axiosInstance.get(`/products?search=${term}&limit=10&page=1`);
        const products = response.data?.data || [];
        setSearchResults(Array.isArray(products) ? products : []);
      } catch (err) {
        console.error('Error buscando productos:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 4) {
      handleSearch(value);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectProduct = (selectedProduct: any) => {
    // Verificar si el producto ya está en los componentes
    const isAlreadyAdded = product.components.some((comp: Component) => comp.sku === selectedProduct.sku);
    
    if (!isAlreadyAdded) {
      // Solo actualizar los componentes, no el producto completo
      const updatedComponents = [...product.components, { 
        sku: selectedProduct.sku, 
        quantity: 1,
        name: selectedProduct.name,
        price: selectedProduct.price
      }];
      setProduct(prevProduct => ({
        ...prevProduct,
        components: updatedComponents
      }));
    }
    setSearchTerm('');
    setShowResults(false);
  };

  const removeComponent = (index: number) => {
    setProduct(prevProduct => ({
      ...prevProduct,
      components: prevProduct.components.filter((_, i) => i !== index)
    }));
  };

  const handleComponentQuantityChange = (index: number, quantity: number) => {
    if (quantity < 1) return; // No permitir cantidades menores a 1
    
    setProduct(prevProduct => ({
      ...prevProduct,
      components: prevProduct.components.map((comp, i) => 
        i === index ? { ...comp, quantity } : comp
      )
    }));
  };

  const addPrecioRow = () => {
    setProduct((prev) => ({
      ...prev,
      precios: [...prev.precios, { codigoPrecio: '', precio: 0 }]
    }))
  }

  const updatePrecioRow = (index: number, patch: Partial<OfimaPrecioItem>) => {
    setProduct((prev) => ({
      ...prev,
      precios: prev.precios.map((row, i) => (i === index ? { ...row, ...patch } : row))
    }))
  }

  const handlePrecioCodigoChange = (index: number, codigo: string) => {
    setProduct((prev) => {
      const base = Number(prev.price) || 0
      return {
        ...prev,
        precios: prev.precios.map((row, i) => {
          if (i !== index) return row
          if (codigo === OFIMA_COD_VENTA_GENERAL) {
            return { ...row, codigoPrecio: codigo, precio: base }
          }
          return { ...row, codigoPrecio: codigo }
        })
      }
    })
  }

  const removePrecioRow = (index: number) => {
    setProduct((prev) => ({
      ...prev,
      precios: prev.precios.filter((_, i) => i !== index)
    }))
  }

  const buildProductBody = (): Record<string, unknown> => {
    const crearOfima = Number(product.crear_en_ofima) === 1
    const body: Record<string, unknown> = {
      ...product,
      is_combo: Boolean(product.is_combo),
      estado: Number(product.estado),
      crear_en_ofima: Number(product.crear_en_ofima)
    }
    if (!crearOfima) {
      delete body.idUnidad
      delete body.precios
    } else {
      body.idUnidad = String(product.idUnidad).trim() || 'UND'
      body.precios = product.precios.map((row) => {
        const codigo = String(row.codigoPrecio).trim()
        const precio =
          codigo === OFIMA_COD_VENTA_GENERAL
            ? Number(product.price) || 0
            : Number(row.precio)
        return { codigoPrecio: codigo, precio }
      })
    }
    if (!isEditMode) {
      delete body.product_id
    } else {
      body.product_id = Number(id)
    }
    return body
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar que si es combo, tenga al menos 2 componentes
    if (product.is_combo && product.components.length < 1) {
      toast.error('Un combo debe tener al menos 1 producto', { icon: "⚠️" });
      return;
    }

    // Validar que todos los componentes tengan cantidad válida
    if (product.is_combo) {
      const invalidComponents = product.components.filter(comp => !comp.quantity || comp.quantity < 1);
      if (invalidComponents.length > 0) {
        toast.error('Todos los componentes deben tener una cantidad válida (mínimo 1)', { icon: "⚠️" });
        return;
      }
    }

    if (!isValidProductTaxRate(product.tax_rate)) {
      toast.error('Seleccione el porcentaje de IVA', { icon: '⚠️' })
      return
    }

    if (Number(product.crear_en_ofima) === 1) {
      if (!product.precios.length) {
        toast.error('Agrega al menos un precio (codigoPrecio y precio) para Ofima', { icon: "⚠️" })
        return
      }
      const precioInvalido = product.precios.some((row) => {
        const cod = String(row.codigoPrecio).trim()
        if (!cod) return true
        const val =
          cod === OFIMA_COD_VENTA_GENERAL ? Number(product.price) : Number(row.precio)
        return !Number.isFinite(val)
      })
      if (precioInvalido) {
        toast.error('Cada precio debe tener codigoPrecio y un precio numérico válido', { icon: "⚠️" })
        return
      }
      const codigos = product.precios.map((p) => String(p.codigoPrecio).trim()).filter(Boolean)
      if (new Set(codigos).size !== codigos.length) {
        toast.error('No puedes repetir el mismo código de lista de precios', { icon: "⚠️" })
        return
      }
    }

    setIsSaving(true)
    try {
      const body = buildProductBody()
      if (isEditMode) {
        const response = await axiosInstance.put('/products', body)
        toast.success('Producto actualizado correctamente ✅', { icon: "🔄" })
        const warnings = response.data?.warnings
        if (Array.isArray(warnings) && warnings.length > 0) {
          toast(String(warnings.join(' · ')), { icon: '⚠️' })
        }
      } else {
        const response = await axiosInstance.post('/products', body)
        toast.success('Producto creado correctamente 🚀', { icon: "✅" })
        const warnings = response.data?.warnings
        if (Array.isArray(warnings) && warnings.length > 0) {
          toast(String(warnings.join(' · ')), { icon: '⚠️' })
        }
      }
      navigate('/products');
    } catch (err: any) {
      console.error('Error guardando producto:', err);
      console.error('Datos del error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.response?.data?.message
      });
      const payload = err.response?.data
      const fromErrors = getApiProductErrorMessage(payload)
      const errorMessage =
        fromErrors ||
        (typeof payload?.message === 'string' ? payload.message : '') ||
        'Error al guardar el producto. Por favor, verifica los datos.'
      toast.error(errorMessage, { icon: '⚠️', duration: 8000 })
    } finally {
      setIsSaving(false)
    }
  };

  return (
    <div className="w-full bg-gray-50 pb-10 pt-4 sm:pt-6">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              outline
              size="sm"
              leftIcon={ArrowLeftIcon}
              onClick={() => navigate('/products')}
              className="shrink-0 border-gray-200 px-2 text-gray-600 hover:bg-gray-100"
              aria-label="Volver al listado de productos"
            >
              <span className="sr-only">Volver</span>
            </Button>
            <h1 className="truncate text-2xl font-semibold text-custom-blue">
              {isEditMode ? 'Editar Producto' : 'Crear Producto'}
            </h1>
          </div>
          {isHydrating ? (
            <div
              className="flex shrink-0 items-center gap-2 text-sm text-gray-600"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-custom-blue border-t-transparent"
                aria-hidden
              />
              Cargando…
            </div>
          ) : (
            <div
              className="flex flex-wrap items-center gap-4 sm:justify-end"
              role="group"
              aria-label="Estado del producto y sincronización Ofima"
            >
              <Switch
                checked={product.estado === 1}
                onChange={(checked) => setProduct({ ...product, estado: checked ? 1 : 0 })}
                label="Producto activo"
              />
              <Switch
                checked={product.crear_en_ofima === 1}
                onChange={(checked) => {
                  const next = checked ? 1 : 0
                  setProduct((prev) => ({
                    ...prev,
                    crear_en_ofima: next,
                    precios:
                      next === 1 && prev.precios.length === 0
                        ? [{ codigoPrecio: '', precio: 0 }]
                        : prev.precios
                  }))
                }}
                label="Crear en Ofima"
              />
            </div>
          )}
        </header>

        <form onSubmit={handleSubmit} className="relative space-y-5" aria-busy={isSaving}>
        {isEditMode && isHydrating ? (
          <div
            className="flex min-h-[280px] flex-col items-center justify-center gap-4 rounded-xl border border-slate-100 bg-white px-6 py-16 shadow-sm"
            role="status"
            aria-live="polite"
            aria-label="Cargando datos del producto"
          >
            <span
              className="h-10 w-10 animate-spin rounded-full border-2 border-custom-blue border-t-transparent"
              aria-hidden
            />
            <p className="text-center text-sm font-medium text-slate-600">Cargando producto…</p>
          </div>
        ) : (
          <>
        {/* Información del Producto */}
        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
          <SectionTitle icon={CubeIcon} className="mb-5">Información del producto</SectionTitle>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-2">
            <FormField label="SKU" required icon={TagIcon}>
              <Input
                type="text"
                name="sku"
                value={product.sku}
                onChange={handleChange}
                required
              />
            </FormField>
            </div>
            <div className="lg:col-span-4">
            <FormField label="Nombre" required icon={CubeIcon}>
              <Input
                type="text"
                name="name"
                value={product.name}
                onChange={handleChange}
                required
              />
            </FormField>
            </div>
            <div className="lg:col-span-2">
            <FormField
              label="Precio"
              required
              icon={CurrencyDollarIcon}
              helpText={
                product.crear_en_ofima === 1
                  ? 'Sin IVA. Es el mismo valor que «Venta General» en lista Ofima.'
                  : 'Sin IVA incluido.'
              }
            >
              <Input
                type="number"
                name="price"
                value={product.price === 0 ? '' : product.price}
                onChange={handleChange}
                required
              />
            </FormField>
            </div>
            <div className="lg:col-span-2">
            <FormField label="IVA (%)" required icon={CurrencyDollarIcon}>
              <Select
                name="tax_rate"
                value={getTaxRateSelectValue(product.tax_rate)}
                onChange={handleSelectChange}
                options={[
                  { value: '', label: 'Seleccione IVA' },
                  { value: '5', label: '5%' },
                  { value: '19', label: '19%' }
                ]}
                required
              />
            </FormField>
            </div>
            <div className="md:col-span-2 lg:col-span-2">
            <FormField label="Descripción">
              <Input
                type="text"
                name="description"
                value={product.description}
                onChange={handleChange}
              />
            </FormField>
            </div>
          </div>
        </div>

        {/* Dimensiones y Peso */}
        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
          <SectionTitle icon={ScaleIcon} className="mb-5">Dimensiones y peso</SectionTitle>
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4 md:gap-6">
            <FormField label="Peso (kg)" icon={ScaleIcon}>
              <Input
                type="text"
                name="peso"
                value={product.peso}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Longitud (cm)" icon={ScaleIcon}>
              <Input
                type="text"
                name="longitud"
                value={product.longitud}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Ancho (cm)" icon={ScaleIcon}>
              <Input
                type="text"
                name="ancho"
                value={product.ancho}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Alto (cm)" icon={ScaleIcon}>
              <Input
                type="text"
                name="alto"
                value={product.alto}
                onChange={handleChange}
              />
            </FormField>
          </div>
        </div>

        {/* Información Adicional */}
        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
          <SectionTitle icon={InformationCircleIcon} className="mb-5">Información adicional</SectionTitle>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
            <FormField label="EAN12" icon={TagIcon}>
              <Input
                type="text"
                name="ean12"
                value={product.ean12}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="URL de la imagen" icon={InformationCircleIcon}>
              <Input
                type="text"
                name="imagen"
                value={product.imagen}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Instrucciones de empaque" icon={InformationCircleIcon}>
              <Input
                type="text"
                name="instrucciones_empaque"
                value={product.instrucciones_empaque}
                onChange={handleChange}
              />
            </FormField>
          </div>
        </div>

        {/* Lista de precios Ofima cuando la sync está activa */}
        {product.crear_en_ofima === 1 && (
        <div className="rounded-xl border border-amber-100/80 bg-gradient-to-br from-amber-50/40 to-white p-5 shadow-sm ring-1 ring-amber-100/60 sm:p-6">
          <SectionTitle icon={InformationCircleIcon} className="mb-5">
            Lista de precios
          </SectionTitle>
            <div className="space-y-4">
              <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">Precios Ofima</h3>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    leftIcon={PlusSmallIcon}
                    onClick={addPrecioRow}
                    disabled={ofimaTodosLosCodigosAsignados}
                    title={
                      ofimaTodosLosCodigosAsignados
                        ? 'Ya usaste todos los códigos de lista de precios disponibles'
                        : undefined
                    }
                  >
                    Agregar precio
                  </Button>
                </div>
                {product.precios.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-amber-200/80 bg-white/80 px-4 py-6 text-center">
                    <p className="text-sm text-slate-600">Necesitas al menos un precio con código y valor.</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="mt-3"
                      leftIcon={PlusSmallIcon}
                      onClick={addPrecioRow}
                      disabled={ofimaTodosLosCodigosAsignados}
                    >
                      Añadir primera fila
                    </Button>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {product.precios.map((row, index) => {
                      const esVentaGeneral =
                        String(row.codigoPrecio).trim() === OFIMA_COD_VENTA_GENERAL
                      const precioMostrado = esVentaGeneral
                        ? product.price
                        : row.precio
                      return (
                      <li
                        key={index}
                        className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:gap-3"
                      >
                        <FormField label="Código precio" className="flex-1 !mb-0 min-w-0" required>
                          <Select
                            name={`codigoPrecio-${index}`}
                            value={row.codigoPrecio}
                            onChange={(e) =>
                              handlePrecioCodigoChange(index, e.target.value)
                            }
                            options={getOfimaPrecioCodigoOptionsForRow(product.precios, index)}
                            required
                            aria-label={`Lista de precios, fila ${index + 1}`}
                          />
                        </FormField>
                        <FormField
                          label="Precio"
                          className="w-full shrink-0 sm:w-40 !mb-0"
                          helpText={esVentaGeneral ? undefined : 'Sin IVA incluido.'}
                        >
                          <Input
                            type="number"
                            value={precioMostrado === 0 ? '' : precioMostrado}
                            onChange={(e) => {
                              if (esVentaGeneral) return
                              const v = parseFloat(e.target.value)
                              updatePrecioRow(index, {
                                precio: Number.isFinite(v) ? v : 0
                              })
                            }}
                            disabled={esVentaGeneral}
                            readOnly={esVentaGeneral}
                            className={esVentaGeneral ? 'bg-slate-50 text-slate-700' : ''}
                            title={
                              esVentaGeneral
                                ? 'Mismo valor que el precio en Información del producto'
                                : undefined
                            }
                            aria-label={`Precio, fila ${index + 1}`}
                          />
                        </FormField>
                        <div className="flex shrink-0 justify-end sm:justify-start sm:pl-0">
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            leftIcon={XMarkIcon}
                            onClick={() => removePrecioRow(index)}
                            className="w-full sm:w-auto"
                          >
                            Quitar
                          </Button>
                        </div>
                      </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
        </div>
        )}

        {/* Configuración de Combo */}
        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
          <SectionTitle icon={CubeIcon} className="mb-4">Combo</SectionTitle>
          <div className="mb-4">
            <Switch
              checked={product.is_combo}
              onChange={(checked) => setProduct({ ...product, is_combo: checked })}
              label="Es un combo"
            />
          </div>

          {product.is_combo && (
            <div className="mt-4 border-t border-slate-100 pt-5">
              <div className="relative">
                <FormField 
                  label="Buscar componente (mín. 4 caracteres)"
                  helpText="Añade los SKU que forman el combo y la cantidad de cada uno."
                  icon={MagnifyingGlassIcon}
                >
                  <div className="relative">
                    <Input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => setShowResults(true)}
                      onBlur={() => setTimeout(() => setShowResults(false), 200)}
                      placeholder="Buscar por SKU o nombre..."
                    />
                  {isSearching && (
                    <div className="absolute right-2 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue"></div>
                    </div>
                  )}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      {searchResults.map((row) => (
                        <div
                          key={row.sku}
                          role="option"
                          aria-selected={false}
                          tabIndex={0}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectProduct(row)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleSelectProduct(row)
                            }
                          }}
                          className="cursor-pointer border-b border-slate-50 px-3 py-2.5 last:border-0 hover:bg-slate-50"
                        >
                          <div className="font-medium text-slate-900">{row.name}</div>
                          <div className="text-xs text-slate-500">SKU: {row.sku}</div>
                          <div className="text-xs font-medium text-custom-blue">
                            ${parseFloat(row.price).toLocaleString('es-CO')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showResults && searchResults.length === 0 && searchTerm.length >= 4 && !isSearching && (
                    <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-500 shadow-lg">
                      No se encontraron resultados
                    </div>
                  )}
                  </div>
                </FormField>
              </div>

              {product.components.length > 0 && (
                <div className="mt-5">
                  <h3 className="mb-3 text-sm font-semibold text-slate-800">Componentes del combo</h3>
                  <ul className="space-y-3">
                    {product.components.map((component, index) => {
                      const fromSearch = searchResults.find((p) => p.sku === component.sku)
                      const displayName =
                        component.name || fromSearch?.name || component.sku
                      const priceRaw = component.price ?? fromSearch?.price
                      const priceNum =
                        priceRaw !== undefined &&
                        priceRaw !== null &&
                        String(priceRaw).trim() !== ''
                          ? parseFloat(String(priceRaw))
                          : NaN
                      const showPrice = Number.isFinite(priceNum)
                      return (
                        <li key={index} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-900">
                                {displayName}
                              </div>
                              <div className="text-sm text-slate-600">
                                SKU: {component.sku}
                              </div>
                              {showPrice && (
                                <div className="text-sm font-medium text-custom-blue">
                                  ${priceNum.toLocaleString('es-CO')}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
                              <div className="flex w-24 shrink-0 flex-col gap-1">
                                <span className="text-xs font-medium text-slate-600" id={`qty-label-${index}`}>
                                  Cantidad
                                </span>
                                <Input
                                  type="number"
                                  min={1}
                                  value={component.quantity}
                                  onChange={(e) => handleComponentQuantityChange(index, parseInt(e.target.value) || 1)}
                                  className="text-center"
                                  aria-labelledby={`qty-label-${index}`}
                                  aria-label={`Cantidad para ${component.sku}`}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="danger"
                                onClick={() => removeComponent(index)}
                                leftIcon={XMarkIcon}
                                size="sm"
                              >
                                Quitar
                              </Button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        </>
        )}

        <div className="sticky bottom-0 z-10 -mx-4 flex flex-col-reverse gap-3 border-t border-slate-200/90 bg-white/95 px-4 py-4 backdrop-blur-sm sm:static sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:rounded-xl sm:border sm:border-slate-100 sm:px-5 sm:shadow-sm">
          <Button
            type="button"
            variant="ghost"
            outline
            className="border-slate-200 text-slate-700 sm:mr-auto"
            onClick={() => navigate('/products')}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant={isEditMode ? 'primary' : 'success'}
            leftIcon={CheckIcon}
            fullWidth
            className={`sm:w-auto sm:min-w-[10.5rem] ${isEditMode ? 'bg-custom-blue hover:bg-custom-blue/90' : ''}`}
            isLoading={isSaving}
            disabled={isSaving || isHydrating}
          >
            {isEditMode ? 'Guardar cambios' : 'Crear producto'}
          </Button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default ProductForm;
